<template>
  <section aria-label="Generated design charts" class="design-charts">
    <h3>Performance charts</h3>
    <p>Calculated from this recommendation and the preview inputs. These are sizing-model curves, not measured pump test-bench results or a replay of the stored trace.</p>
    <TabView v-model:active-index="activeChart" scrollable data-cy="ai-chart-tabs">
      <TabPanel header="Pump Performance">
        <section v-if="activeChart === 0" aria-label="Pump performance chart">
          <p>Head, brake horsepower and hydraulic efficiency versus flow at the recommended operating speed. BEP marks best-efficiency flow; efficiency is read from the efficiency curve's axis.</p>
          <p v-if="calculations.descendingOrderPumps.value.length > 1">This is the combined stack view using the reference pump's BEP. Review Pump Sections for each section's local operating points.</p>
          <SinglePumpChart :scenario-calculations="calculations" title="Calculated Pump Performance" :minimum-height-px="380" draw-legend is-horizontal hide-pump-selector :npsh-mode="false" />
        </section>
      </TabPanel>
      <TabPanel header="Pump Sections">
        <AIPumpCurves v-if="activeChart === 1" :calculations="calculations" />
      </TabPanel>
      <TabPanel header="Well Inflow (IPR)">
        <section v-if="activeChart === 2" aria-label="Well inflow chart">
          <p>Calculated well inflow versus pressure. Use this to assess the production target against the well's deliverability.</p>
          <IPRChart :scenario-calculations="calculations" :minimum-height-px="380" draw-legend is-horizontal />
        </section>
      </TabPanel>
      <TabPanel header="Gas Profile">
        <section v-if="activeChart === 3" aria-label="Gas profile chart">
          <p>Calculated gas conditions through the pump. Review this alongside section curves when gas compression changes the local flow.</p>
          <GasCurveChart :scenario-calculations="calculations" :minimum-height-px="380" draw-legend is-horizontal display-pump-thresholds hide-threshold-control />
        </section>
      </TabPanel>
    </TabView>
  </section>
</template>

<script setup lang="ts">
import { ref } from "vue";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import SinglePumpChart from "@/components/scenarios/charts/SinglePumpChart.vue";
import IPRChart from "@/components/scenarios/charts/IPRChart.vue";
import GasCurveChart from "@/components/scenarios/charts/GasCurveChart.vue";
import AIPumpCurves from "./AIPumpCurves.vue";

defineProps<{ calculations: ScenarioCalculations }>();
const activeChart = ref(0);
</script>

<style scoped>
.design-charts { margin: 1.5rem 0; min-width: 0; }
.design-charts p { color: var(--text-color-secondary); }
</style>
